import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from "@angular/core";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { s25Resource } from "../../../util/s25.resource";
import { S25Util } from "../../../util/s25-util";
import { TrailApi, UnacknowledgedDto } from "./trail.api";
import { TrailLabels } from "./trail.labels";

@TypeManagerDecorator("s25-ng-trail-celebration")
@Component({
    selector: "s25-ng-trail-celebration",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule],
    template: `
        @if (showOverlay()) {
            <div class="trail-celebration__overlay" (click)="acknowledge()">
                <div class="trail-celebration__modal" (click)="$event.stopPropagation()">
                    <div class="trail-celebration__confetti" aria-hidden="true">🎉</div>
                    <h2 class="trail-celebration__heading">Congratulations!</h2>

                    @if (unacknowledgedData()?.summits?.length) {
                        <section class="trail-celebration__section">
                            <h3>{{ labels.SUMMITS }} Earned</h3>
                            <ul class="trail-celebration__list">
                                @for (summit of unacknowledgedData()!.summits; track summit.summitEarnedId) {
                                    <li class="trail-celebration__item">
                                        <span class="trail-celebration__item-name">{{ summit.trailName }}</span>
                                    </li>
                                }
                            </ul>
                        </section>
                    }

                    @if (unacknowledgedData()?.defns?.length) {
                        <section class="trail-celebration__section">
                            <h3>{{ labels.AWARDS }} Earned</h3>
                            <ul class="trail-celebration__list">
                                @for (defn of unacknowledgedData()!.defns; track defn.defnEarnedId) {
                                    <li class="trail-celebration__item">
                                        <span class="trail-celebration__item-name">{{
                                            defn.endingAwardDisplayName || defn.defnName
                                        }}</span>
                                        <span class="trail-celebration__item-sub">{{ defn.curricName }}</span>
                                    </li>
                                }
                            </ul>
                        </section>
                    }

                    <button
                        type="button"
                        class="trail-celebration__ack-btn"
                        [disabled]="acknowledging()"
                        (click)="acknowledge()"
                    >
                        @if (acknowledging()) {
                            Saving…
                        } @else {
                            Got it!
                        }
                    </button>
                </div>
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: block;
            }
            .trail-celebration__overlay {
                position: fixed;
                inset: 0;
                z-index: 10000;
                display: flex;
                align-items: center;
                justify-content: center;
                background: rgba(0, 0, 0, 0.5);
                animation: trail-fade-in 0.25s ease;
            }
            .trail-celebration__modal {
                background: #fff;
                border-radius: 12px;
                padding: 2rem;
                max-width: 420px;
                width: 90%;
                text-align: center;
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
                animation: trail-scale-in 0.3s ease;
            }
            .trail-celebration__confetti {
                font-size: 3rem;
                margin-bottom: 0.5rem;
            }
            .trail-celebration__heading {
                margin: 0 0 1rem;
                font-size: 1.5rem;
                color: var(--cn-navy, #1e3a5f);
            }
            .trail-celebration__section {
                margin-bottom: 1rem;
                text-align: left;
            }
            .trail-celebration__section h3 {
                margin: 0 0 0.5rem;
                font-size: 1rem;
                color: var(--cn-navy, #1e3a5f);
            }
            .trail-celebration__list {
                list-style: none;
                margin: 0;
                padding: 0;
                display: flex;
                flex-direction: column;
                gap: 0.4rem;
            }
            .trail-celebration__item {
                display: flex;
                flex-direction: column;
                padding: 0.4rem 0.6rem;
                border-radius: 6px;
                background: #f0f7ff;
            }
            .trail-celebration__item-name {
                font-weight: 600;
                font-size: 0.9rem;
            }
            .trail-celebration__item-sub {
                font-size: 0.8rem;
                color: var(--color--text-muted);
            }
            .trail-celebration__ack-btn {
                margin-top: 1rem;
                padding: 0.6rem 1.5rem;
                border: none;
                border-radius: 6px;
                background: var(--cn-navy, #1e3a5f);
                color: #fff;
                font-size: 1rem;
                cursor: pointer;
                font-weight: 600;
            }
            .trail-celebration__ack-btn:disabled {
                opacity: 0.6;
                cursor: not-allowed;
            }
            @keyframes trail-fade-in {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }
            @keyframes trail-scale-in {
                from {
                    transform: scale(0.9);
                    opacity: 0;
                }
                to {
                    transform: scale(1);
                    opacity: 1;
                }
            }
        `,
    ],
})
export class TrailCelebrationComponent {
    readonly labels = TrailLabels;
    readonly acknowledging = signal(false);

    readonly reloadToken = input(0);
    readonly acknowledged = output<void>();

    readonly unacknowledgedRes = s25Resource({
        params: () => this.reloadToken(),
        loader: () => TrailApi.getUnacknowledged(),
    });

    readonly unacknowledgedData = computed((): UnacknowledgedDto | undefined => this.unacknowledgedRes.value());

    readonly showOverlay = computed((): boolean => {
        const data = this.unacknowledgedData();
        if (!data) return false;
        return data.summits?.length > 0 || data.defns?.length > 0;
    });

    async acknowledge(): Promise<void> {
        if (this.acknowledging()) return;
        const data = this.unacknowledgedData();
        if (!data) return;

        this.acknowledging.set(true);
        try {
            const summitIds = (data.summits ?? []).map((summit) => summit.summitEarnedId);
            const defnIds = (data.defns ?? []).map((defn) => defn.defnEarnedId);
            await TrailApi.acknowledge(summitIds, defnIds);
            this.acknowledged.emit();
            this.unacknowledgedRes.resource.reload();
        } catch (caughtError) {
            S25Util.showError(caughtError, "Unable to acknowledge");
        } finally {
            this.acknowledging.set(false);
        }
    }
}
